import { Component, OnInit, PLATFORM_ID, inject } from '@angular/core';
import { CommonModule, isPlatformServer } from '@angular/common';
import { DomSanitizer, SafeHtml, Title, Meta } from '@angular/platform-browser';
import { MainLayout } from '../../layouts/main-layout';
import {
  LucideAngularModule,
  ChevronDownIcon,
  ChevronUpIcon,
} from 'lucide-angular';
import { faqs } from '../../assets/docs/preguntas-frecuentes';
import { MarginWrapper } from '../../layouts/margin-wrapper';

interface MenuSection {
  id: string;
  title: string;
  level: number;
  isOpen: boolean;
  children: MenuSection[];
}

@Component({
  selector: 'allas-preguntas-frecuentes',
  imports: [MainLayout, MarginWrapper, CommonModule, LucideAngularModule],
  template: `
    <allas-main-layout [showPopularSections]="false" [showNewsletter]="true">
      <allas-margin-wrapper styleClass="fs-condensed">
        <!-- Header -->
        <section class="bg-white border-b border-gray-200 py-8">
          <div class="container mx-auto px-4">
            <div class="max-w-7xl mx-auto">
              <h1 class="text-3xl font-bold text-gray-900 mb-4">
                Preguntas Frecuentes
              </h1>
              <p class="text-gray-600">
                Encuentra respuestas a las preguntas más comunes sobre nuestros
                productos y servicios
              </p>
            </div>
          </div>
        </section>

        <!-- Content -->
        <div class="py-2">
          <!-- Search -->
          <div
            class="mb-8 sticky z-10 bg-white py-4 border-b border-gray-200 top-16 xl:top-[75px]"
          >
            <input
              type="text"
              placeholder="Buscar en preguntas frecuentes..."
              (input)="filterContent($event)"
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm"
            />
          </div>

          <!-- Content Layout -->
          <div class="flex flex-col lg:flex-row gap-4 lg:gap-6">
            <!-- Mobile Menu Toggle -->
            <div class="lg:hidden mb-4">
              <button
                (click)="toggleMobileMenu()"
                class="w-full bg-white border border-gray-200 rounded-lg px-4 py-3 flex items-center justify-between text-gray-700 font-medium"
              >
                <span>{{
                  showMobileMenu ? 'Ocultar índice' : 'Mostrar índice'
                }}</span>
                <svg
                  class="w-5 h-5 transform transition-transform"
                  [class.rotate-180]="showMobileMenu"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 9l-7 7-7-7"
                  ></path>
                </svg>
              </button>
            </div>

            <!-- Sidebar Menu -->
            <div
              class="lg:w-1/4 w-full"
              [class.hidden]="!showMobileMenu"
              [class.lg:block]="true"
            >
              <div
                class="bg-white rounded-lg shadow-sm border border-gray-200 lg:sticky lg:top-6"
              >
                <div class="p-3 sm:p-4 border-b border-gray-200">
                  <h2 class="text-base sm:text-lg font-semibold text-gray-900">
                    Índice
                  </h2>
                </div>
                <div
                  class="p-3 sm:p-4 space-y-1 max-h-[60vh] lg:max-h-[calc(100vh-200px)] overflow-y-auto"
                >
                  <ng-container *ngFor="let section of menuSections">
                    <!-- Nivel 1 -->
                    <div>
                      <button
                        (click)="toggleSection(section)"
                        class="w-full text-left px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-gray-700 hover:bg-gray-100 hover:text-blue-600 rounded-md flex items-center justify-between transition-colors"
                        [class.text-blue-600]="section.isOpen"
                      >
                        <span
                          (click)="scrollToSection(section.id, $event)"
                          class="flex-1 text-left truncate"
                          [title]="section.title"
                        >
                          {{ section.title }}
                        </span>
                        <svg
                          class="w-4 h-4 transform transition-transform flex-shrink-0 ml-2"
                          [class.rotate-180]="section.isOpen"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M19 9l-7 7-7-7"
                          ></path>
                        </svg>
                      </button>

                      <!-- Nivel 2 (Subniveles) -->
                      <div
                        *ngIf="section.isOpen && section.children.length > 0"
                        class="ml-3 sm:ml-4 mt-1 space-y-1"
                      >
                        <button
                          *ngFor="let child of section.children"
                          (click)="scrollToSection(child.id, $event)"
                          class="w-full text-left px-2 sm:px-3 py-1.5 text-xs sm:text-sm text-gray-600 hover:bg-gray-50 hover:text-blue-600 rounded transition-colors block truncate"
                          [title]="child.title"
                        >
                          {{ child.title }}
                        </button>
                      </div>
                    </div>
                  </ng-container>
                </div>
              </div>
            </div>

            <!-- Content Area -->
            <div class="lg:w-3/4 w-full">
              <div class="rounded-lg shadow-sm">
                <div class="p-4 sm:p-6 lg:p-8">
                  <div
                    class="prose prose-gray max-w-none prose-headings:scroll-mt-6"
                    [innerHTML]="htmlContent"
                    #contentArea
                  ></div>
                </div>
              </div>
            </div>
          </div>

          <!-- Contact Section -->
          <div class="mt-12 bg-blue-50 rounded-lg p-6">
            <h3 class="text-xl font-bold text-gray-900 mb-4">
              ¿No encontraste tu respuesta?
            </h3>
            <p class="text-gray-700 mb-4">
              Nuestro equipo de atención al cliente está listo para ayudarte
            </p>
            <div class="grid md:grid-cols-2 gap-4">
              <div>
                <p><strong>Teléfono:</strong> +504 2283-4540</p>
                <p><strong>Email:</strong> ventas&#64;allasrepuestos.com</p>
              </div>
              <div>
                <p><strong>Horario:</strong> Lunes a Viernes 8AM - 5PM</p>
                <p><strong>Sábados:</strong> 8AM - 3PM</p>
              </div>
            </div>
          </div>
        </div>
      </allas-margin-wrapper>
    </allas-main-layout>
  `,
  styles: [
    `
      :host {
        display: block;
      }

      /* Estilos para el contenido markdown */
      ::ng-deep .prose {
        font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI',
          Roboto, sans-serif;
        line-height: 1.6;
      }

      ::ng-deep .prose h2 {
        @apply text-3xl font-bold text-gray-900 mt-8 mb-4 pb-2 border-b border-gray-200;
        scroll-margin-top: 2rem;
      }

      ::ng-deep .prose h4 {
        @apply text-2xl font-semibold text-gray-800 mt-6 mb-3;
        scroll-margin-top: 2rem;
      }

      ::ng-deep .prose h6 {
        @apply text-xl font-medium text-gray-700 mt-4 mb-2;
        scroll-margin-top: 2rem;
      }

      ::ng-deep .prose p {
        @apply text-gray-600 mb-4;
      }

      ::ng-deep .prose ul,
      ::ng-deep .prose ol {
        @apply mb-4 ml-6;
      }

      ::ng-deep .prose li {
        @apply mb-2 text-gray-600;
      }

      ::ng-deep .prose strong {
        @apply font-semibold text-gray-800;
      }

      ::ng-deep .prose a {
        @apply text-blue-600 hover:text-blue-800 underline;
      }

      ::ng-deep .prose table {
        @apply w-full border-collapse border border-gray-300 my-4;
      }

      ::ng-deep .prose th,
      ::ng-deep .prose td {
        @apply border border-gray-300 px-4 py-2 text-left;
      }

      ::ng-deep .prose th {
        @apply bg-gray-50 font-semibold;
      }

      ::ng-deep .prose blockquote {
        @apply border-l-4 border-blue-500 pl-4 italic text-gray-700 my-4;
      }

      /* Responsive adjustments */
      @media (max-width: 1024px) {
        ::ng-deep .prose h2 {
          @apply text-2xl;
        }

        ::ng-deep .prose h4 {
          @apply text-xl;
        }

        ::ng-deep .prose h6 {
          @apply text-lg;
        }
      }
    `,
  ],
})
export class PreguntasFrecuentes implements OnInit {
  private title = inject(Title);
  private meta = inject(Meta);

  readonly chevronDownIcon = ChevronDownIcon;
  readonly chevronUpIcon = ChevronUpIcon;

  htmlContent: SafeHtml = '';
  menuSections: MenuSection[] = [];
  showMobileMenu = false;
  searchTerm = '';

  constructor(private sanitizer: DomSanitizer) {}

  ngOnInit() {
    this.setMetaTags();
    this.loadContent();
  }

  private setMetaTags() {
    const pageTitle = 'Preguntas Frecuentes | Allas Repuestos';
    const description =
      'Encuentra respuestas a las preguntas más comunes sobre productos, servicios, envíos y garantías en Allas Repuestos. Atención al cliente en Honduras.';
    const imageUrl = 'https://www.allasrepuestos.com/logos/isotipo.webp';

    this.title.setTitle(pageTitle);

    this.meta.updateTag({
      name: 'description',
      content: description,
    });

    this.meta.updateTag({
      property: 'og:title',
      content: pageTitle,
    });

    this.meta.updateTag({
      property: 'og:description',
      content: description,
    });

    this.meta.updateTag({
      property: 'og:type',
      content: 'website',
    });

    this.meta.updateTag({
      property: 'og:image',
      content: imageUrl,
    });

    this.meta.updateTag({
      name: 'twitter:card',
      content: 'summary_large_image',
    });

    this.meta.updateTag({
      name: 'twitter:title',
      content: pageTitle,
    });

    this.meta.updateTag({
      name: 'twitter:description',
      content: description,
    });
  }

  private loadContent() {
    const markdownText = faqs;
    const { html, sections } = this.parseMarkdownToHtml(markdownText);

    this.htmlContent = this.sanitizer.bypassSecurityTrustHtml(html);
    this.menuSections = sections;

    // Manejar hash inicial si existe
    this.handleInitialHash();
  }

  private parseMarkdownToHtml(markdownText: string): {
    html: string;
    sections: MenuSection[];
  } {
    const lines = markdownText.split('\n');
    const htmlParts: string[] = [];
    const sections: MenuSection[] = [];
    let currentSection: MenuSection | null = null;
    let inList = false;
    let listType = '';
    let inTable = false;
    let tableRows: string[] = [];
    let tableHeaders: string[] = [];

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmedLine = line.trim();

      if (!trimmedLine) {
        if (inList) {
          htmlParts.push(`</${listType}>`);
          inList = false;
        }
        if (inTable) {
          htmlParts.push(this.generateTableHtml(tableHeaders, tableRows));
          inTable = false;
          tableHeaders = [];
          tableRows = [];
        }
        htmlParts.push('<br>');
        continue;
      }

      // Detectar tablas (líneas que contienen pipes)
      if (trimmedLine.includes('|') && trimmedLine.split('|').length > 2) {
        if (!inTable) {
          inTable = true;
          // Cerrar listas si estaban abiertas
          if (inList) {
            htmlParts.push(`</${listType}>`);
            inList = false;
          }
        }

        const cells = trimmedLine
          .split('|')
          .map((cell) => cell.trim())
          .filter((cell) => cell.length > 0);

        // Si es la primera línea de la tabla, son headers
        if (tableHeaders.length === 0) {
          tableHeaders = cells;
        } else if (trimmedLine.match(/^[\|\s\-\:]+$/)) {
          // Línea de separación (| ----- | ----- |), la ignoramos
          continue;
        } else {
          // Es una fila de datos
          tableRows.push(trimmedLine);
        }
        continue;
      }

      // Si estábamos en una tabla y encontramos una línea sin pipes, cerrar la tabla
      if (inTable) {
        htmlParts.push(this.generateTableHtml(tableHeaders, tableRows));
        inTable = false;
        tableHeaders = [];
        tableRows = [];
      }

      // Headings
      if (trimmedLine.startsWith('# ')) {
        if (inList) {
          htmlParts.push(`</${listType}>`);
          inList = false;
        }

        const title = trimmedLine.replace('# ', '').replace(/\*\*/g, '').trim();
        const id = this.generateId(title);

        const section: MenuSection = {
          id,
          title,
          level: 1,
          isOpen: true,
          children: [],
        };
        sections.push(section);
        currentSection = section;

        const h2Html = `<h2 id="${id}">${title}</h2>`;
        htmlParts.push(h2Html);
      } else if (trimmedLine.startsWith('## ')) {
        if (inList) {
          htmlParts.push(`</${listType}>`);
          inList = false;
        }

        const title = trimmedLine
          .replace('## ', '')
          .replace(/\*\*/g, '')
          .trim();
        const id = this.generateId(title);

        // Para FAQs, los H2 son las secciones principales
        const section: MenuSection = {
          id,
          title,
          level: 1,
          isOpen: true,
          children: [],
        };
        sections.push(section);
        currentSection = section;

        const h4Html = `<h4 id="${id}">${title}</h4>`;
        htmlParts.push(h4Html);
      } else if (trimmedLine.startsWith('### ')) {
        if (inList) {
          htmlParts.push(`</${listType}>`);
          inList = false;
        }

        const title = trimmedLine
          .replace('### ', '')
          .replace(/\*\*/g, '')
          .trim();
        const id = this.generateId(title);

        if (currentSection) {
          const childSection: MenuSection = {
            id,
            title,
            level: 2,
            isOpen: false,
            children: [],
          };
          currentSection.children.push(childSection);
        }

        const h6Html = `<h6 id="${id}">${title}</h6>`;
        htmlParts.push(h6Html);
      }
      // Lists
      else if (trimmedLine.match(/^[\*\-]\s/)) {
        if (!inList) {
          htmlParts.push('<ul>');
          inList = true;
          listType = 'ul';
        }

        const content = trimmedLine.replace(/^[\*\-]\s/, '').trim();
        htmlParts.push(`<li>${this.processInlineFormatting(content)}</li>`);
      } else if (trimmedLine.match(/^\d+\.\s/)) {
        if (!inList) {
          htmlParts.push('<ol>');
          inList = true;
          listType = 'ol';
        }

        const content = trimmedLine.replace(/^\d+\.\s/, '').trim();
        htmlParts.push(`<li>${this.processInlineFormatting(content)}</li>`);
      }
      // Paragraphs
      else {
        if (inList) {
          htmlParts.push(`</${listType}>`);
          inList = false;
        }

        htmlParts.push(`<p>${this.processInlineFormatting(trimmedLine)}</p>`);
      }
    }

    // Cerrar listas si quedan abiertas
    if (inList) {
      htmlParts.push(`</${listType}>`);
    }

    // Cerrar tabla si queda abierta
    if (inTable) {
      htmlParts.push(this.generateTableHtml(tableHeaders, tableRows));
    }

    const finalHtml = htmlParts.join('\n');

    return {
      html: finalHtml,
      sections,
    };
  }

  private generateTableHtml(headers: string[], rows: string[]): string {
    let tableHtml =
      '<table class="w-full border-collapse border border-gray-300 my-4">';

    // Generar headers
    tableHtml += '<thead><tr>';
    for (const header of headers) {
      tableHtml += `<th class="border border-gray-300 px-4 py-2 text-left bg-gray-50 font-semibold">${this.processInlineFormatting(
        header
      )}</th>`;
    }
    tableHtml += '</tr></thead>';

    // Generar filas de datos
    tableHtml += '<tbody>';
    for (const row of rows) {
      const cells = row
        .split('|')
        .map((cell) => cell.trim())
        .filter((cell) => cell.length > 0);

      tableHtml += '<tr>';
      for (const cell of cells) {
        tableHtml += `<td class="border border-gray-300 px-4 py-2 text-left">${this.processInlineFormatting(
          cell
        )}</td>`;
      }
      tableHtml += '</tr>';
    }
    tableHtml += '</tbody></table>';

    return tableHtml;
  }

  private processInlineFormatting(text: string): string {
    // Bold
    text = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    // Italic
    text = text.replace(/\*(.*?)\*/g, '<em>$1</em>');
    // Links
    text = text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>');

    return text;
  }

  private generateId(title: string): string {
    return title
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  }

  private handleInitialHash() {
    if (typeof window === 'undefined') return;

    const hash = window.location.hash.substring(1);
    if (hash) {
      setTimeout(() => {
        const targetElement = document.getElementById(hash);
        if (targetElement) {
          const headerHeight = 80;
          const elementTop = targetElement.offsetTop - headerHeight - 20;

          window.scrollTo({
            top: elementTop,
            behavior: 'smooth',
          });
        }
      }, 500);
    }
  }

  toggleSection(section: MenuSection) {
    section.isOpen = !section.isOpen;
  }

  toggleMobileMenu() {
    this.showMobileMenu = !this.showMobileMenu;
  }

  scrollToSection(sectionId: string, event: Event) {
    if (typeof window === 'undefined') return;

    event.preventDefault();
    event.stopPropagation();

    if (window.innerWidth < 1024) {
      this.showMobileMenu = false;
    }

    window.history.replaceState(null, '', `preguntas-frecuentes#${sectionId}`);

    const scrollToElement = () => {
      const targetElement = document.getElementById(sectionId);
      if (targetElement) {
        const headerHeight = 80;
        const elementTop = targetElement.offsetTop - headerHeight - 20;

        window.scrollTo({
          top: elementTop,
          behavior: 'smooth',
        });
        return true;
      }
      return false;
    };

    if (!scrollToElement()) {
      setTimeout(() => {
        if (!scrollToElement()) {
          setTimeout(scrollToElement, 300);
        }
      }, 100);
    }
  }

  filterContent(event: any): void {
    const searchTerm = event.target.value.toLowerCase().trim();
    this.searchTerm = searchTerm;

    if (!searchTerm) {
      this.loadContent();
      return;
    }

    // Filtrar contenido basado en el término de búsqueda
    const markdownText = faqs;
    const { html, sections } = this.parseMarkdownToHtml(markdownText);

    // Buscar en el HTML procesado (sin tags HTML)
    const htmlText = this.stripHtmlTags(html).toLowerCase();
    const hasMatch = htmlText.includes(searchTerm);

    if (hasMatch) {
      // Si hay coincidencias, mostrar todo el contenido pero resaltado
      const highlightedHtml = this.highlightSearchTerm(html, searchTerm);
      this.htmlContent =
        this.sanitizer.bypassSecurityTrustHtml(highlightedHtml);
      this.menuSections = sections; // Mantener todas las secciones

      // Hacer scroll a la primera coincidencia después de que se renderice el contenido
      setTimeout(() => {
        this.scrollToFirstMatch(searchTerm);
      }, 100);
    } else {
      // Si no hay coincidencias, mostrar mensaje de "no se encontraron resultados"
      this.htmlContent = this.sanitizer.bypassSecurityTrustHtml(
        '<div class="text-center py-12"><p class="text-gray-500 text-lg">No se encontraron resultados para tu búsqueda.</p><p class="text-gray-400 mt-2">Intenta con otros términos.</p></div>'
      );
      this.menuSections = []; // Ocultar el menú cuando no hay resultados
    }
  }

  private scrollToFirstMatch(searchTerm: string): void {
    if (typeof window === 'undefined') return;
    // Buscar el primer elemento con el resaltado amarillo
    const highlightedElements = document.querySelectorAll('.bg-yellow-200');

    if (highlightedElements.length > 0) {
      const firstMatch = highlightedElements[0] as HTMLElement;
      const headerHeight = 80;
      const elementTop = firstMatch.offsetTop - headerHeight - 20;

      window.scrollTo({
        top: elementTop,
        behavior: 'smooth',
      });
    }
  }

  private stripHtmlTags(html: string): string {
    // Remover tags HTML para buscar solo en el texto
    return html.replace(/<[^>]*>/g, '');
  }

  private highlightSearchTerm(html: string, searchTerm: string): string {
    if (!searchTerm) return html;

    // Crear una expresión regular que sea insensible a mayúsculas/minúsculas
    const regex = new RegExp(`(${this.escapeRegExp(searchTerm)})`, 'gi');

    // Reemplazar las coincidencias con un span que tenga fondo amarillo
    return html.replace(
      regex,
      '<span class="bg-yellow-200 px-1 rounded">$1</span>'
    );
  }

  private escapeRegExp(string: string): string {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }
}
